/**
 * Nomenclature Service
 * Fetches and caches nomenclatures (makes, models, fuels, gearboxes, categories)
 */
const NomenclatureService = (function () {
    "use strict";

    // Memory cache (same page session)
    const memoryCache = {};

    // sessionStorage key prefix
    const STORAGE_PREFIX = "nomenclatures_";

    /**
     * Get nomenclatures for a vehicle class
     * @param {string} vehicleClass - Vehicle class slug (autoturisme, motociclete, etc.)
     * @returns {Promise<Object>} - Nomenclatures object
     */
    async function get(vehicleClass = "autoturisme") {
        const cacheKey = STORAGE_PREFIX + vehicleClass;

        // Layer 1: Memory cache (instant)
        if (memoryCache[vehicleClass]) {
            return memoryCache[vehicleClass];
        }

        // Layer 2: sessionStorage
        try {
            const stored = sessionStorage.getItem(cacheKey);
            if (stored) {
                const data = JSON.parse(stored);
                memoryCache[vehicleClass] = data;
                return data;
            }
        } catch (e) {
            // sessionStorage might be disabled or quota exceeded
            console.warn("sessionStorage unavailable:", e);
        }

        // Otherwise fetch from backend
        try {
            const response = await fetch(`/autocomplete/nomenclatures/${vehicleClass}`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            // Store in memory cache
            memoryCache[vehicleClass] = data;

            // Store in sessionStorage
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify(data));
            } catch (e) {
                console.warn("Failed to store in sessionStorage:", e);
            }

            return data;
        } catch (error) {
            console.error("Failed to fetch nomenclatures:", error);
            // Return empty default structure on error
            return {
                makes: [],
                fuels: [],
                gearboxes: [],
                categories: [],
                years: { min: 2000, max: new Date().getFullYear() },
            };
        }
    }

    /**
     * Clear cached nomenclatures
     * @param {string|null} vehicleClass - Specific class to clear, or null for all
     */
    function clearCache(vehicleClass = null) {
        if (vehicleClass) {
            delete memoryCache[vehicleClass];
            try {
                sessionStorage.removeItem(STORAGE_PREFIX + vehicleClass);
            } catch (e) {
                // Ignore
            }
        } else {
            // Clear all
            Object.keys(memoryCache).forEach((key) => delete memoryCache[key]);
            try {
                Object.keys(sessionStorage)
                    .filter((key) => key.startsWith(STORAGE_PREFIX))
                    .forEach((key) => sessionStorage.removeItem(key));
            } catch (e) {
                // Ignore
            }
        }
    }

    return { get, clearCache };
})();
